import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import client, { unwrap } from "@/api/client";

// Every cached copy of the attention feed: topbar bell, dashboard section, notifications page.
const FEED_KEYS = ["dashboard-attention-badge", "dashboard-attention", "notifications-all"];
const isFeedQuery = (q) => FEED_KEYS.includes(q.queryKey[0]);

export function useNotificationFeed({ queryKey = ["dashboard-attention-badge"], limit, enabled = true, refetchInterval } = {}) {
  return useQuery({
    queryKey,
    queryFn: () => client.get("/dashboard/attention", { params: limit ? { limit } : undefined }).then(unwrap),
    enabled,
    refetchInterval,
  });
}

// Read state is optimistic across every cached feed, then reconciled with the server.
export function useMarkRead() {
  const queryClient = useQueryClient();
  const optimistic = (isTarget) => async () => {
    await queryClient.cancelQueries({ predicate: isFeedQuery });
    const snapshot = queryClient.getQueriesData({ predicate: isFeedQuery });
    queryClient.setQueriesData({ predicate: isFeedQuery }, (old) =>
      Array.isArray(old) ? old.map((i) => (isTarget(i) ? { ...i, read: true } : i)) : old,
    );
    return { snapshot };
  };
  const rollback = (_err, _vars, ctx) => ctx?.snapshot?.forEach(([key, data]) => queryClient.setQueryData(key, data));
  const settle = () => queryClient.invalidateQueries({ predicate: isFeedQuery });

  const markRead = useMutation({
    mutationFn: (keys) => client.post("/dashboard/attention/read", { keys }),
    onMutate: (keys) => optimistic((i) => keys.includes(i.key))(),
    onError: rollback,
    onSettled: settle,
  });
  const markAllRead = useMutation({
    mutationFn: () => client.post("/dashboard/attention/read-all"),
    onMutate: optimistic(() => true),
    onError: rollback,
    onSettled: settle,
  });

  return {
    markRead: (item) => !item.read && item.key && markRead.mutate([item.key]),
    markAllRead: () => markAllRead.mutate(),
  };
}
